/**
 * A script's workflow as pages to flick through: step zero, then each step,
 * each drawn as the Scripts view draws it, all sharing one frame so the
 * drawing stays put from page to page. A PDF printed on A4 landscape, or a
 * PowerPoint deck of the same pages, for people without the app.
 */
import type { DesignDocument } from "../design/document";
import type { ScriptDocument } from "../design/protocol";
import { pptx } from "../render/pptx";
import {
  composeStills,
  downloadBlob,
  pngFile,
  printPdf,
  stillCaption,
  stillFilename,
  type StillLegend,
  type StillSheet,
  type StillSource,
} from "../render/still";
import { autoFluidColour, fluidColours } from "../render/SvgRenderer";
import { THEME } from "../render/theme";
import type { Scene } from "../scene";
import { finishText } from "../scripts/operations";
import type { StepPreview } from "../scripts/preview";
import type { LiquidUnits } from "./units";

export type StepsFormat = "pdf" | "pptx";

export const STEPS_EXPORTS: readonly { format: StepsFormat; label: string }[] = [
  { format: "pdf", label: "PDF, A4 landscape, a page per step" },
  { format: "pptx", label: "PowerPoint, A4 landscape, a slide per step" },
];

/** PNG pixels per screen pixel on a slide: sharp when the slide is printed. */
const SLIDE_SCALE = 3;
/** As the drawing's bands are: liquid more solid than gas. */
const LIQUID_OPACITY = 0.55;
const GAS_OPACITY = 0.3;

export interface StepPage {
  /** Step zero, then each step in turn. */
  preview: StepPreview;
  heading: string;
  footer: string[];
}

/** Step zero and each step: what each page shows and says. */
export function stepPages(
  script: ScriptDocument,
  previewAt: (at: number) => StepPreview | null,
  design: DesignDocument,
  parentName: string | null,
  units?: LiquidUnits,
): StepPage[] {
  const steps = script.steps ?? [];
  const names = Object.fromEntries((design.nodes ?? []).map((node) => [node.local_id, node.label || node.local_id]));
  const zero = previewAt(-1);
  const pages: StepPage[] = [];
  if (zero) {
    pages.push({
      preview: zero,
      heading: `Step 0 of ${steps.length} · ${parentName ? `After ${parentName}` : "Initial conditions"}`,
      footer: script.description?.trim() ? [script.description.trim()] : [],
    });
  }
  steps.forEach((step, index) => {
    const preview = previewAt(index);
    if (!preview) return;
    pages.push({
      preview,
      heading: `Step ${index + 1} of ${steps.length} · ${step.label}`,
      footer: [`Ends: ${finishText(step, names, units)}`, ...(step.notes?.trim() ? [step.notes.trim()] : [])],
    });
  });
  return pages;
}

/**
 * The colours the pages draw, the same key on every page: each fluid any
 * step holds, then pressure and vacuum where any step has them.
 */
export function colourKey(previews: readonly StepPreview[]): StillLegend[] {
  if (previews.length === 0) return [];
  // Colours follow the condition set's fluids, so they agree from step to step.
  const colours = fluidColours(previews[0]);
  const fluids = new Set<string>();
  const gases = new Set<string>();
  for (const preview of previews) {
    const places = [...Object.values(preview.nodes), ...Object.values(preview.edges).flat()];
    for (const place of places) {
      if (place.liquid) fluids.add(place.liquid);
      else gases.add(place.gas);
    }
  }
  // A fluid outside the condition set takes the next colour, as the drawing gives it.
  for (const fluid of fluids) {
    if (!colours.has(fluid)) colours.set(fluid, autoFluidColour(colours.size));
  }
  const label = (fluid: string) => previews[0]!.fluidLabels[fluid] ?? fluid;
  return [
    ...[...colours.keys()]
      .filter((fluid) => fluids.has(fluid))
      .map((fluid) => ({ label: label(fluid), swatch: { fill: colours.get(fluid)!, opacity: LIQUID_OPACITY } })),
    ...(gases.has("pressure") ? [{ label: "Pressure", swatch: { fill: THEME.previewPressure, opacity: GAS_OPACITY } }] : []),
    ...(gases.has("vacuum") ? [{ label: "Vacuum", swatch: { fill: THEME.previewVacuum, opacity: GAS_OPACITY } }] : []),
  ];
}

/** Each page's sheet: the step as heading, the design and its version beside it, the key at the right. */
export function stepSheets(
  pages: readonly StepPage[],
  draw: (preview: StepPreview) => Scene,
  source: StillSource,
  scriptName: string,
): StillSheet[] {
  const design = stillCaption(source);
  const key = colourKey(pages.map((page) => page.preview));
  return pages.map((page) => ({
    scene: draw(page.preview),
    caption: {
      title: page.heading,
      status: `${scriptName} · ${design.title} · ${design.status}`,
      legend: key,
      footer: page.footer,
    },
  }));
}

export function stepsFilename(source: StillSource, scriptName: string, format: StepsFormat): string {
  return stillFilename({ ...source, name: `${source.name} ${scriptName} steps` }, format);
}

export async function exportSteps(
  sheets: readonly StillSheet[],
  filename: string,
  format: StepsFormat,
): Promise<void> {
  if (sheets.length === 0) throw new Error("The script has no steps to export.");
  const stills = await composeStills(sheets);
  if (format === "pdf") {
    await printPdf(stills, filename, "A4");
    return;
  }
  const slides = [];
  for (const [index, still] of stills.entries()) {
    const png = new Uint8Array(await (await pngFile(still, SLIDE_SCALE)).arrayBuffer());
    const { title, status } = sheets[index]!.caption;
    slides.push({ png, width: still.width, height: still.height, description: `${title}. ${status}` });
  }
  const deck = pptx(slides, filename.replace(/\.pptx$/, ""));
  downloadBlob(
    new Blob([deck], { type: "application/vnd.openxmlformats-officedocument.presentationml.presentation" }),
    filename,
  );
}
